import classNames from "classnames";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { goalTypeLabel } from "@/shared/domain/agents";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";
import {
  type AgentThreadGoalRecord,
  AgentThreadGoalStatus,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  goalHasFetchableOutcome,
  outcomeForGoal,
  shouldFetchToolDetail,
} from "@/shared/domain/agentThread/goalContent";
import { useToolDetails } from "@/shared/domain/agentThread/hooks";
import { ChevronRight, Target } from "@/shared/foundation/icons";
import {
  CheckCircleOutlineIcon,
  ErrorOutlineIcon,
  HourglassEmptyIcon,
} from "@/shared/foundation/icons/mui";
import { Popover } from "@/shared/foundation/primitives";

import { GoalDetails } from "./GoalDetails";
import styles from "./ThreadGoalsControl.module.css";

interface ThreadGoalsControlProps {
  thread: AgentThreadRecord;

  /**
   * `pill` renders icon plus an achieved count, for the thread page's header.
   * `icon` renders the icon alone at 20px, for the chat sidebar's row of
   * actions. Same split as the thread visibility control beside it.
   */
  variant: "pill" | "icon";
}

const STATUS_ICON = {
  [AgentThreadGoalStatus.PENDING]: HourglassEmptyIcon,
  [AgentThreadGoalStatus.ACHIEVED]: CheckCircleOutlineIcon,
  [AgentThreadGoalStatus.FAILED]: ErrorOutlineIcon,
} as const;

const STATUS_LABEL = {
  [AgentThreadGoalStatus.PENDING]: "Pending",
  [AgentThreadGoalStatus.ACHIEVED]: "Achieved",
  [AgentThreadGoalStatus.FAILED]: "Failed",
} as const;

const STATUS_CLASS = {
  [AgentThreadGoalStatus.PENDING]: styles.isPending,
  [AgentThreadGoalStatus.ACHIEVED]: styles.isAchieved,
  [AgentThreadGoalStatus.FAILED]: styles.isFailed,
} as const;

/** The status the trigger's dot shows for the whole set: any failure wins,
 *  then anything still open, and only an all-achieved set reads as done. */
function overallStatus(goals: AgentThreadGoalRecord[]): AgentThreadGoalStatus {
  if (goals.some((goal) => goal.status === AgentThreadGoalStatus.FAILED)) {
    return AgentThreadGoalStatus.FAILED;
  }
  if (goals.some((goal) => goal.status === AgentThreadGoalStatus.PENDING)) {
    return AgentThreadGoalStatus.PENDING;
  }
  return AgentThreadGoalStatus.ACHIEVED;
}

interface GoalRowProps {
  goal: AgentThreadGoalRecord;
  threadId: string;
  defaultExpanded: boolean;
}

/**
 * One goal: a row with its status and name that expands to show what it asked
 * for and what came of it. The achieve-tool detail behind the outcome is
 * fetched the first time the row expands, so a thread pays nothing for goals
 * nobody opens.
 */
function GoalRow({ goal, threadId, defaultExpanded }: GoalRowProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const hasFetchableOutcome = goalHasFetchableOutcome(goal);
  const toolUseId = hasFetchableOutcome
    ? (goal.achieve_tool_use_id ?? undefined)
    : undefined;

  const { data, isFetching, error, refetch } = useToolDetails(
    threadId,
    toolUseId,
  );
  const outcome = React.useMemo(
    () => (data ? outcomeForGoal(goal, data) : null),
    [goal, data],
  );

  React.useEffect(() => {
    // The query is declared with `enabled: false`; `shouldFetchToolDetail`
    // keeps this from re-issuing while a fetch is in flight, after it has
    // resolved, or after it has failed.
    if (
      expanded &&
      shouldFetchToolDetail({ toolUseId, data, isFetching, error })
    ) {
      void refetch();
    }
  }, [expanded, toolUseId, data, isFetching, error, refetch]);

  const StatusIcon = STATUS_ICON[goal.status];
  const detailsId = React.useId();

  return (
    <li className={styles.goal}>
      <button
        type="button"
        className={styles.goalRow}
        aria-expanded={expanded}
        aria-controls={detailsId}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <ChevronRight
          className={classNames(styles.chevron, {
            [styles.isExpanded]: expanded,
          })}
          aria-hidden="true"
        />
        <StatusIcon
          className={classNames(styles.statusIcon, STATUS_CLASS[goal.status])}
          aria-hidden="true"
        />
        <span className={styles.goalLabel}>
          {goalTypeLabel(goal.goal_type)}
        </span>
        <span
          className={classNames(styles.statusText, STATUS_CLASS[goal.status])}
        >
          {STATUS_LABEL[goal.status]}
        </span>
      </button>
      {expanded && (
        <div id={detailsId} className={styles.goalDetails}>
          <GoalDetails
            goal={goal}
            outcome={outcome}
            isLoadingOutcome={isFetching}
            canHaveOutcome={hasFetchableOutcome}
          />
        </div>
      )}
    </li>
  );
}

/**
 * The goals an agent thread was launched with, behind one header control.
 *
 * Goals are set when the thread starts and each resolves once, so they are
 * reference material rather than live progress; a header control keeps them
 * one click away without taking space from the conversation. The trigger's dot
 * summarizes the set, and the popup lists every goal with its details. Renders
 * nothing for a thread without goals.
 */
export function ThreadGoalsControl({
  thread,
  variant,
}: ThreadGoalsControlProps) {
  const goals = thread.goals ?? [];
  if (goals.length === 0) {
    return null;
  }

  const achieved = goals.filter(
    (goal) => goal.status === AgentThreadGoalStatus.ACHIEVED,
  ).length;
  const status = overallStatus(goals);
  const description = `Goals: ${achieved} of ${goals.length} achieved`;
  const iconOnly = variant === "icon";

  const trigger = (
    <button
      type="button"
      className={iconOnly ? styles.iconButton : styles.pill}
      aria-label={description}
    >
      <span className={styles.triggerIconWrap}>
        <Target
          className={iconOnly ? styles.icon : styles.pillIcon}
          aria-hidden="true"
        />
        <span
          className={classNames(styles.dot, STATUS_CLASS[status])}
          aria-hidden="true"
        />
      </span>
      {iconOnly ? null : `Goals ${achieved}/${goals.length}`}
    </button>
  );

  return (
    <Popover>
      <Popover.Trigger
        render={<RobotoTooltip title={description}>{trigger}</RobotoTooltip>}
      />
      <Popover.Content
        align="end"
        aria-label="Thread goals"
        className={styles.popup}
      >
        <div className={styles.popupHeader}>
          <span className={styles.popupTitle}>Goals</span>
          <span className={styles.popupSummary}>
            {`${achieved} of ${goals.length} achieved`}
          </span>
        </div>
        <ul className={styles.goals}>
          {goals.map((goal, index) => (
            <GoalRow
              key={`${goal.message_sequence_num}-${index}`}
              goal={goal}
              threadId={thread.thread_id}
              defaultExpanded={goals.length === 1}
            />
          ))}
        </ul>
      </Popover.Content>
    </Popover>
  );
}
